import React from 'react';
import PropTypes from 'prop-types';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING,
} from 'common/applicationAssemblyLineConstants';

import {
  getListingAvailableDateRange,
} from 'common/listingHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrInfoTile, {
  SR_INFO_TILE_THEME_ADMIN_GREY,
} from 'client/components/SrInfoTile/SrInfoTile';

import momentWithAustinTimezone from 'moment';

const SrApplicationAssemblyLineStepMoveInDate = ({
  assemblyLine,
  householdApplication,
}) => {
  const status = SrApplicationAssemblyLineStepMoveInDate.getStatus({
    assemblyLine,
    householdApplication,
  });

  const { startDate, endDate } = getListingAvailableDateRange({
    listing: assemblyLine.listing,
    tenantScreeningCriteria: assemblyLine.tenantScreeningCriteria,
  });

  const dateRangeDom = (
    <SrBox display='inlineBlock'>
      <SrFlex
        border='1px solid #EAEBEC'
        borderRadius='8px'
        padding='16px'
      >
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label='Applicant move-in date'
          value={momentWithAustinTimezone(householdApplication.moveInDate).format('MMM D')}
          valueColor={SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING[status]}
        />
        <SrBox height='35px' width='1px' backgroundColor='#EAEBEC' margin='0 24px' />
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label='Move-in date range'
          value={
            `${momentWithAustinTimezone(startDate).format('MMM D')} - ` +
            `${momentWithAustinTimezone(endDate).format('MMM D')}`
          }
        />
      </SrFlex>
    </SrBox>
  );

  const informationRequiredDom = (
    <SrBox marginTop='22px'>
      <SrText
        color='orange300'
        font='greycliff'
        weight='700'
        size='18'
        lineHeight='24px'
        display='block'
      >
        Information Required
      </SrText>
      <SrBox marginTop='8px' maxWidth='468px'>
        <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
          Applicant Move-In Date:
          <br />
          Please ask the applicant to change move-in date to a day within the move-in date deadline.
        </SrErrorMessage>
        <SrBox marginTop='16px' />
        <SrText
          color='orange300'
          font='greycliff'
          weight='600'
          size='16'
          lineHeight='24px'
        >
          Once you've asked the applicant for these changes move on to the next applicant in this household.
        </SrText>
      </SrBox>
    </SrBox>
  );

  return (
    <SrBox minWidth='300px'>
      {dateRangeDom}
      {status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS && informationRequiredDom}
    </SrBox>
  );
};

SrApplicationAssemblyLineStepMoveInDate.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  householdApplication: PropTypes.object.isRequired,
};

SrApplicationAssemblyLineStepMoveInDate.stepTitle = 'Move-in Date';
SrApplicationAssemblyLineStepMoveInDate.displayName = 'SrApplicationAssemblyLineStepMoveInDate';

SrApplicationAssemblyLineStepMoveInDate.getStatus = ({
  householdApplication,
  assemblyLine,
}) => {
  const { startDate, endDate } = getListingAvailableDateRange({
    listing: assemblyLine.listing,
    tenantScreeningCriteria: assemblyLine.tenantScreeningCriteria,
  });
  let status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;

  const qualifiedMoveInDate = (
    momentWithAustinTimezone(householdApplication.moveInDate).isSameOrAfter(startDate)
    && momentWithAustinTimezone(householdApplication.moveInDate).isSameOrBefore(endDate)
  );

  if (qualifiedMoveInDate) {
    status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
  }

  return status;
};

export default SrApplicationAssemblyLineStepMoveInDate;
